import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { PlusCircle, Trash2 } from "lucide-react";

export default function AdminRooms() {
  const [rooms, setRooms] = useState([]);
  const [newRoom, setNewRoom] = useState({ title: "", available: "" });

  // ✅ Updated API URL
  const API_URL = "https://backend.ferejhotel.com/api/rooms";

  // ✅ Fetch rooms
  const fetchRooms = async () => {
    try {
      const res = await axios.get(API_URL);
      setRooms(res.data);
    } catch (err) {
      console.error("Error fetching rooms:", err);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  // ✅ Add new room
  const handleAddRoom = async () => {
    if (!newRoom.title || !newRoom.available) {
      return alert("Please fill in all fields");
    }

    try {
      await axios.post(API_URL, {
        title: newRoom.title,
        available: parseInt(newRoom.available, 10),
      });

      fetchRooms(); // ✅ Re-fetch instead of using return data
      setNewRoom({ title: "", available: "" });
    } catch (err) {
      console.error("Error adding room:", err);
    }
  };

  // ✅ Update room
  const handleUpdate = async (id, newAvailable) => {
    if (!newAvailable) return;

    try {
      await axios.put(`${API_URL}/${id}`, {
        available: parseInt(newAvailable, 10),
      });

      fetchRooms(); // ✅ re-fetch to update UI
    } catch (err) {
      console.error("Error updating room:", err);
    }
  };

  // ✅ Delete room
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this room?")) return;

    try {
      await axios.delete(`${API_URL}/${id}`);
      fetchRooms(); // ✅ re-fetch to update UI
    } catch (err) {
      console.error("Error deleting room:", err);
    }
  };

  return (
    <motion.section
      className="min-h-screen bg-gradient-to-br from-black via-[#0f0f0f] to-[#1c1c1c] text-white py-20 px-6 font-[Poppins]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl font-semibold text-yellow-400 mb-12 text-center font-[Playfair_Display] drop-shadow-[0_2px_10px_rgba(255,215,0,0.4)]">
          🏨 Admin Room Control Panel
        </h2>

        {/* Add Room */}
        <motion.div
          className="bg-[#111]/80 border border-yellow-500/30 backdrop-blur-md p-8 rounded-2xl shadow-[0_0_20px_rgba(255,215,0,0.1)] mb-16"
          whileHover={{ scale: 1.01 }}
        >
          <h3 className="text-2xl font-semibold text-yellow-400 mb-6 font-[Playfair_Display]">
            Add a New Room
          </h3>

          <div className="flex flex-col md:flex-row items-center gap-4">
            <input
              type="text"
              placeholder="Room Title"
              value={newRoom.title}
              onChange={(e) =>
                setNewRoom({ ...newRoom, title: e.target.value })
              }
              className="w-full md:w-1/2 p-3 rounded-lg bg-black/60 border border-yellow-400/40 text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 outline-none transition"
            />
            <input
              type="number"
              placeholder="Available Rooms"
              value={newRoom.available}
              onChange={(e) =>
                setNewRoom({ ...newRoom, available: e.target.value })
              }
              className="w-full md:w-1/3 p-3 rounded-lg bg-black/60 border border-yellow-400/40 text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 outline-none transition"
            />
            <button
              onClick={handleAddRoom}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold px-6 py-3 rounded-lg transition shadow-md hover:shadow-yellow-400/50"
            >
              <PlusCircle size={20} /> Add
            </button>
          </div>
        </motion.div>

        {/* Rooms List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {rooms.length > 0 ? (
            rooms.map((room, index) => (
              <motion.div
                key={room._id}
                className="bg-[#111]/70 border border-yellow-400/30 backdrop-blur-md p-6 rounded-2xl shadow-[0_0_15px_rgba(255,215,0,0.08)] hover:shadow-[0_0_25px_rgba(255,215,0,0.25)] transition-all duration-500"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <h4 className="text-2xl font-semibold text-yellow-400 font-[Playfair_Display] mb-3">
                  {room.title}
                </h4>
                <p className="text-gray-300 mb-4">
                  <span className="font-medium text-yellow-300">Available:</span>{" "}
                  {room.available}
                </p>

                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    placeholder="Update availability"
                    onChange={(e) =>
                      handleUpdate(room._id, e.target.value)
                    }
                    className="flex-1 p-2 rounded-lg bg-black/50 border border-yellow-400/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 outline-none transition"
                  />
                  <button
                    onClick={() => handleDelete(room._id)}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition shadow-md hover:shadow-red-500/40"
                  >
                    <Trash2 size={18} /> Delete
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            <p className="col-span-full text-center text-gray-400 text-lg">
              No rooms added yet.
            </p>
          )}
        </div>
      </div>
    </motion.section>
  );
}
